import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from './context/AuthContext';
import { toast } from './components/ui/use-toast';
import { checkAndSetVersionMismatch } from './lib/versionHelper';

const axiosInstance = axios.create();
let interceptorSetup = null; // Promise to track the interceptor setup

// Function to check if the user is online
const checkOnlineStatus = () => {
  return navigator.onLine;
};

const useAxiosInterceptors = () => {
  const { auth, fetchAccessToken, logout, loading } = useContext(AuthContext);
  const [interceptorReady, setInterceptorReady] = useState(false);
  const [isOnline, setIsOnline] = useState(checkOnlineStatus());

  let temp = auth.accessToken ? auth.accessToken : null;

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    temp = auth.accessToken;
  }, [auth]);

  useEffect(() => {
    if (loading) return;

    interceptorSetup = new Promise((resolve) => {
      const setAuthHeader = (config) => {
        // Check online status before proceeding with the request
        if (!navigator.onLine) {
          // Create a standardized offline error
          const error = new Error('You are currently offline');
          error.isOfflineError = true;

          // Abort the request
          const controller = new AbortController();
          controller.abort();

          return Promise.reject(error);
        }

        // Access the current auth state from the ref
        if (temp) {
          config.headers['Authorization'] = `Bearer ${temp}`;
        }
        return config;
      };

      const requestInterceptor = axiosInstance.interceptors.request.use(
        setAuthHeader,
        (error) => {
          if (error.isOfflineError) {
            toast({
              variant: 'destructive',
              title: 'Ingen internetanslutning',
              description: 'Du är offline. Kontrollera din internetanslutning och försök igen.',
            });
          }
          return Promise.reject(error);
        }
      );

      const responseInterceptor = axiosInstance.interceptors.response.use(
        (response) => {
          if (response.data) {
            checkAndSetVersionMismatch(response.data);
          }
          return response;
        },
        async (error) => {
          const originalRequest = error.config;
          if (error.response && error.response.status === 429) {
            toast({
              variant: 'destructive',
              title: 'Något gick fel.',
              description: 'Du har gjort för många serveranrop på kort tid och loggas därför ut av säkerhetsskäl. Försök igen om en stund.',
            });
            logout();
            return Promise.reject(error);
          }

          // Check if this is a network error (like ERR_INTERNET_DISCONNECTED)
          if (!navigator.onLine) {
            error.isOfflineError = true;
            toast({
              variant: 'destructive',
              title: 'Ingen internetanslutning',
              description: 'Du är offline. Kontrollera din internetanslutning och försök igen.',
            });
            return Promise.reject(error);
          }

          if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
            toast({
              variant: 'destructive',
              title: 'Något gick fel.',
              description: 'Ett oväntat nätverksfel inträffade.'
            });
            return Promise.reject(error)
          }

          if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
              const newAccessToken = await fetchAccessToken(localStorage.getItem('refreshToken'));
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              temp = newAccessToken;
              return axiosInstance(originalRequest);
            } catch (tokenRefreshError) {
              console.error('Token refresh failed', tokenRefreshError);
              logout();
            }
          }

          return Promise.reject(error);
        }
      );

      resolve(); // Resolves when interceptors are set up
      setInterceptorReady(true); // Mark interceptors as ready in state

      return () => {
        axiosInstance.interceptors.request.eject(requestInterceptor);
        axiosInstance.interceptors.response.eject(responseInterceptor);
      };
    });
  }, [auth, loading]);

  return { interceptorReady, isOnline };
};

export { useAxiosInterceptors, axiosInstance, interceptorSetup };